import DataKeys from 'k8s/DataKeys'
import getDataSelector from 'core/utils/getDataSelector'
import { createSharedSelector } from 'core/utils/selectorHelpers'
import {
  allVirtualMachinesLiteByIdSelector,
  virtualMachinesLiteByIdSelector,
} from 'openstack/components/vms/selectors'
import {
  allTenantsSelector,
  userTenantsSelector,
} from 'app/plugins/account/components/userManagement/tenants/selectors'
import { emptyObj } from 'utils/fp'
import { volumeSnapshotsSelector } from '../snapshots/selectors'
import { latestMessageByVolumeSelector } from '../messages/selectors'

export const volumeTransfersSelector = createSharedSelector(
  getDataSelector<DataKeys.VolumeTransfers>(DataKeys.VolumeTransfers),
  (volumeTransfers) => {
    return volumeTransfers
  },
)

export const allVolumeTransfersSelector = createSharedSelector(
  getDataSelector<DataKeys.AllVolumeTransfers>(DataKeys.AllVolumeTransfers),
  (volumeTransfers) => {
    return volumeTransfers
  },
)

export const volumesSelector = createSharedSelector(
  getDataSelector<DataKeys.OpenstackVolumes>(DataKeys.OpenstackVolumes),
  virtualMachinesLiteByIdSelector,
  allTenantsSelector,
  userTenantsSelector,
  volumeTransfersSelector,
  volumeSnapshotsSelector,
  latestMessageByVolumeSelector,
  (
    volumes,
    vmsById,
    allTenants,
    userTenants,
    volumeTransfers,
    volumeSnapshots,
    latestMessageByVolume,
  ) => {
    const volumeTransfersByVolumeId = volumeTransfers.reduce((accum, transfer) => {
      return {
        ...accum,
        [transfer?.volume_id]: transfer,
      }
    }, {})
    const volumeSnapshotsByVolumeId = volumeSnapshots.reduce((accum, snapshot) => {
      if (accum[snapshot?.volume_id]) {
        return {
          ...accum,
          [snapshot?.volume_id]: [...accum[snapshot.volume_id], snapshot],
        }
      }
      return {
        ...accum,
        [snapshot?.volume_id]: [snapshot],
      }
    }, {})
    return volumes.map((volume) => {
      // any model changes go here
      const vmAttachment = volume?.attachments?.length
        ? volume.attachments.find((attachment) => !!attachment.server_id)
        : null
      const vm = vmsById[vmAttachment?.server_id]
      const withoutPool = volume?.['os-vol-host-attr:host']?.split('#')?.[0] || ''
      const [hostId, backend] = withoutPool?.split('@')
      return {
        ...volume,
        name: volume?.name || volume?.id,
        created_at: `${volume.created_at}Z`,
        attachedVm: vm,
        device: vmAttachment?.device,
        tenantName: [...userTenants, ...allTenants]?.find(
          (tenant) => tenant.id === volume?.['os-vol-tenant-attr:tenant_id'],
        )?.name,
        transfer: volumeTransfersByVolumeId[volume?.id],
        snapshots: volumeSnapshotsByVolumeId[volume?.id] || [],
        sourceSnapshot: volume?.snapshot_id ? volumeSnapshotsByVolumeId[volume?.snapshot_id] : null,
        hostId,
        backend,
        message: latestMessageByVolume[volume?.id],
      }
    })
  },
)

export const allVolumesSelector = createSharedSelector(
  getDataSelector<DataKeys.AllOpenstackVolumes>(DataKeys.AllOpenstackVolumes),
  allVirtualMachinesLiteByIdSelector,
  allTenantsSelector,
  userTenantsSelector,
  allVolumeTransfersSelector,
  volumeSnapshotsSelector,
  latestMessageByVolumeSelector,
  (
    volumes,
    vmsById,
    allTenants,
    userTenants,
    volumeTransfers,
    volumeSnapshots,
    latestMessageByVolume,
  ) => {
    const volumeTransfersByVolumeId = volumeTransfers.reduce((accum, transfer) => {
      return {
        ...accum,
        [transfer?.volume_id]: transfer,
      }
    }, {})
    const volumeSnapshotsByVolumeId = volumeSnapshots.reduce((accum, snapshot) => {
      if (accum[snapshot?.volume_id]) {
        return {
          ...accum,
          [snapshot?.volume_id]: [...accum[snapshot.volume_id], snapshot],
        }
      }
      return {
        ...accum,
        [snapshot?.volume_id]: [snapshot],
      }
    }, {})
    return volumes.map((volume) => {
      // any model changes go here
      const vmAttachment = volume?.attachments?.length
        ? volume.attachments.find((attachment) => !!attachment.server_id)
        : null
      const vm = vmsById[vmAttachment?.server_id]
      const withoutPool = volume?.['os-vol-host-attr:host']?.split('#')?.[0] || ''
      const [hostId, backend] = withoutPool?.split('@')
      return {
        ...volume,
        created_at: `${volume.created_at}Z`,
        attachedVm: vm,
        device: vmAttachment?.device,
        tenantName: [...userTenants, ...allTenants]?.find(
          (tenant) => tenant.id === volume?.['os-vol-tenant-attr:tenant_id'],
        )?.name,
        transfer: volumeTransfersByVolumeId[volume?.id],
        snapshots: volumeSnapshotsByVolumeId[volume?.id] || [],
        sourceSnapshot: volume?.snapshot_id ? volumeSnapshotsByVolumeId[volume?.snapshot_id] : null,
        hostId,
        backend,
        message: latestMessageByVolume[volume?.id],
      }
    })
  },
)

export const singleVolumeSelector = createSharedSelector(
  getDataSelector<DataKeys.SingleOpenstackVolume>(DataKeys.SingleOpenstackVolume),
  virtualMachinesLiteByIdSelector,
  allTenantsSelector,
  userTenantsSelector,
  volumeTransfersSelector,
  latestMessageByVolumeSelector,
  (volumes, vmsById, allTenants, userTenants, volumeTransfers, latestMessageByVolume) => {
    const volumeTransfersByVolumeId = volumeTransfers.reduce((accum, transfer) => {
      return {
        ...accum,
        [transfer?.volume_id]: transfer,
      }
    }, {})
    return volumes.map((volume) => {
      // any model changes go here
      const vmAttachment = volume?.attachments?.length
        ? volume.attachments.find((attachment) => !!attachment.server_id)
        : null
      const vm = vmsById[vmAttachment?.server_id]
      return {
        ...volume,
        name: volume?.name || volume?.id,
        created_at: `${volume.created_at}Z`,
        attachedVm: vm,
        device: vmAttachment?.device,
        tenantName: [...userTenants, ...allTenants]?.find(
          (tenant) => tenant.id === volume?.['os-vol-tenant-attr:tenant_id'],
        )?.name,
        transfer: volumeTransfersByVolumeId[volume?.id],
        message: latestMessageByVolume[volume?.id],
      }
    })
  },
)

export const volumesLiteSelector = createSharedSelector(
  getDataSelector<DataKeys.OpenstackVolumesLite>(DataKeys.OpenstackVolumesLite),
  (vms) => vms,
)

export const volumesLiteByIdSelector = createSharedSelector(volumesLiteSelector, (vms) =>
  vms.reduce((accum, vm) => {
    return {
      ...accum,
      [vm.id]: vm,
    }
  }, emptyObj),
)

export const volumesByIdSelector = createSharedSelector(volumesSelector, (volumes) => {
  return volumes.reduce((accum, volume) => {
    return {
      ...accum,
      [volume.id]: volume,
    }
  }, {})
})
